import { Box, Tooltip, Typography } from "@mui/material";
import { alpha, lighten, Theme } from "@mui/material/styles";
import { ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { percentageNoDecimals } from "../../shared/percentage";
import { ValueByClaimType } from "../../shared/PortfolioTypes.ts";
import {
  BAR_FILL_ALPHA,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import ClaimTypeChip from "../issues/ClaimTypeChip";
import { getBlendedColor } from "../issues/claimTypeColors";
import { gainShares } from "./firmShares";
import { allCasesOf, CaseGroup } from "./perCase";

/** The height of the track of a row, in px. */
export const TRACK_HEIGHT = 6;

/** A share below this is too small to round to a whole percent. */
const TINY_SHARE = 0.01;

/** How far the colour of all the cases is lightened from the primary colour. */
const ALL_CASES_LIGHTEN = 0.2;

/**
 * The colour of a group of cases: that of its claim types, or of all the
 * cases.
 */
export const fillOf = (group: CaseGroup, theme: Theme): string =>
  group.kind === "set"
    ? getBlendedColor(group.claimTypes, theme)
    : lighten(theme.palette.primary.main, ALL_CASES_LIGHTEN);

/** The colour of a loss, whatever the claim types. */
export const lossFill = (theme: Theme): string =>
  alpha(theme.palette.data.negative.main, BAR_FILL_ALPHA);

/**
 * A row of a list by claim types: the claim types, or the average of all
 * the cases, with the number of cases; an amount and what stands beside it;
 * and a track across the row with what is drawn on it. What the row has more
 * to tell is in its tooltip, which the whole row opens and which points at
 * what it tells of on the track, so that it stands within its own row.
 */
export const ClaimTypeRow = ({
  group,
  size,
  amount,
  aside,
  track,
  tooltip,
}: {
  group: CaseGroup;
  size: number;
  amount: number;
  aside?: ReactNode;
  track: ReactNode;
  /** `at` is where on the track it points, in percent of the track's width. */
  tooltip?: { text: string; at: number };
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "ClaimTypeBreakdown" });
  const trackRef = useRef<HTMLDivElement>(null);

  const row = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `minmax(0, 1fr) auto ${theme.spacing(4.5)}`,
        columnGap: 1.5,
        rowGap: 0.75,
        alignItems: "center",
        py: 1.25,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
        }}
      >
        {group.kind === "set" ? (
          group.claimTypes.map((type) => (
            <ClaimTypeChip key={type} type={type} />
          ))
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t("average")}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
        >
          {t("cases", { count: size })}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          textAlign: "right",
          fontWeight: 700,
          color: amount < 0 ? toneColors(theme).adverse : undefined,
          ...TABULAR_NUMERALS,
        }}
      >
        {currency.format(amount)}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          textAlign: "right",
          color: "text.secondary",
          ...TABULAR_NUMERALS,
        }}
      >
        {aside}
      </Typography>
      <Box
        ref={trackRef}
        sx={{
          gridColumn: "1 / -1",
          position: "relative",
          height: TRACK_HEIGHT,
          borderRadius: 999,
          bgcolor: "action.hover",
        }}
      >
        {track}
      </Box>
    </Box>
  );

  if (!tooltip) return row;

  // a point of no width on the track, read anew wherever the track has moved
  const pointOnTrack = () => {
    const trackElement = trackRef.current!;
    return {
      contextElement: trackElement,
      getBoundingClientRect: () => {
        const { left, top, width, height } =
          trackElement.getBoundingClientRect();
        return new DOMRect(left + (width * tooltip.at) / 100, top, 0, height);
      },
    };
  };

  return (
    <Tooltip
      title={tooltip.text}
      placement="top"
      arrow
      describeChild
      slotProps={{ popper: { anchorEl: pointOnTrack } }}
    >
      {row}
    </Tooltip>
  );
};

/**
 * A set of claim types with what its cases bring together and its share of
 * the whole, or what a case brings on average over all of them.
 */
type Row = CaseGroup & { value: number; size: number; share?: number };

/**
 * What the cases with each set of claim types bring, a row per set: the claim
 * types and the number of cases, the amount and its share of the whole, and a
 * bar against the largest of the amounts. What a case brings on average over
 * all the cases is among them, each row placed by its amount in the order
 * given.
 */
export const ClaimTypeBreakdown = ({
  data,
  sort,
}: {
  data: ValueByClaimType[];
  sort: (a: { value: number }, b: { value: number }) => number;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "ClaimTypeBreakdown" });

  const shares = gainShares(data.map(({ value }) => value));
  const allCases = allCasesOf(data);
  const rows: Row[] = [
    ...data.map(
      ({ claimTypes, value, size }, index): Row => ({
        kind: "set",
        claimTypes,
        value,
        size,
        share: shares[index],
      }),
    ),
    // a single case is its own average; an average is no part of the whole,
    // so it has no share of it
    ...(allCases && allCases.size > 1
      ? [
          {
            kind: "all" as const,
            value: allCases.average,
            size: allCases.size,
          },
        ]
      : []),
  ].sort(sort);
  const largest = Math.max(0, ...rows.map(({ value }) => Math.abs(value)));

  return (
    <div>
      {rows.map((row) => (
        <ClaimTypeRow
          key={row.kind === "set" ? row.claimTypes.join() : row.kind}
          group={row}
          size={row.size}
          amount={row.value}
          // a loss is no part of the whole, so it has no share of it
          aside={
            row.share !== undefined &&
            (row.share < TINY_SHARE
              ? t("tinyShare")
              : percentageNoDecimals.format(row.share))
          }
          track={
            <Box
              sx={{
                width:
                  largest > 0 ? `${(Math.abs(row.value) / largest) * 100}%` : 0,
                height: "100%",
                borderRadius: 999,
                bgcolor: row.value < 0 ? lossFill(theme) : fillOf(row, theme),
              }}
            />
          }
        />
      ))}
    </div>
  );
};
